import React, { useRef, useState } from 'react'
import Input from 'components/input'
import Menu, { MenuProps } from 'components/menu'
import { ChevronDown, ChevronUp, Cross, iconHeight } from 'components/icons'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { numberWithCommas } from 'components/spreadsheet/helpers/format'
import { ITableRow } from 'types'
import { IViewPortColumn } from 'components/spreadsheet/types'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import Checkbox from 'components/checkbox'
import Spinner from 'components/spinner'

interface SearchMenuProps extends MenuProps {
  columns: IViewPortColumn[]
}

const SearchMenu: React.FC<SearchMenuProps> = ({ id, menuState, setMenuState, width, columns }) => {
  const { spreadsheetData, setSpreadsheetData, filterSearchRows, setFilterSearchRows } = useDataContext()
  const [search, setSearch] = useState<string>(spreadsheetData.searchTerm)

  const searchTimeout = useRef<TimeoutID | null>(null)

  const handleSearchChange = (search: string) => {
    setSearch(search)
    if (searchTimeout.current !== null) cancelTimeout(searchTimeout.current)

    searchTimeout.current = requestTimeout(() => {
      if (search !== '') {
        setSpreadsheetData({ type: 'SEARCH_TABLE', searchTerm: search })
      } else {
        setSpreadsheetData({ type: 'CLEAR_SEARCH' })
      }
    }, 1000)
  }

  const selectNext = (forward: boolean) => {
    // Use visible rows so rowNumber indices match the rendered grid
    const bitmap = spreadsheetData.visibleBitmap
    const visibleRows: ITableRow[] = bitmap ? spreadsheetData.rows.filter((_, i) => bitmap[i]) : spreadsheetData.rows

    const interval = forward ? 1 : -1
    if (spreadsheetData.selectedSearchCell.rowId) {
      const rowIndex = visibleRows.findIndex(
        (row: ITableRow) => row.publicId === spreadsheetData.selectedSearchCell.rowId
      )
      const row = visibleRows.find((row: ITableRow) => row.publicId === spreadsheetData.selectedSearchCell.rowId)
      if (row) {
        const searchedColumns = spreadsheetData.searchedRows.get(row.publicId) || []
        const columnIndex = searchedColumns.findIndex(
          (columnId: string) => columnId === spreadsheetData.selectedSearchCell.columnId
        )

        // If the next searched value is the next column
        const nextColumn = searchedColumns[columnIndex + interval]
        if (nextColumn) {
          setSpreadsheetData({
            type: 'SET_SELECTED_SEARCH_CELL',
            rowId: row.publicId,
            columnId: nextColumn,
            columnNumber: columnIndex + interval,
            rowNumber: rowIndex
          })
          return
        }

        // Else find the next row containing a non-zero length searched array
        let newRowFound = false
        let testRowIndex = rowIndex + interval
        while (!newRowFound) {
          if (testRowIndex === rowIndex) {
            newRowFound = true
          } else {
            if (testRowIndex > visibleRows.length - 1) testRowIndex = 0
            else if (testRowIndex < 0) testRowIndex = visibleRows.length - 1
            const testRow = visibleRows[testRowIndex]
            const testRowSearched = spreadsheetData.searchedRows.get(testRow?.publicId || '')
            if (testRow && testRowSearched && testRowSearched.length !== 0) {
              const columnId = forward ? testRowSearched[0] : testRowSearched[testRowSearched.length - 1]
              const columnIndex = columns.findIndex((column) => column.column.publicId === columnId)
              setSpreadsheetData({
                type: 'SET_SELECTED_SEARCH_CELL',
                rowId: testRow.publicId,
                columnId: columnId,
                columnNumber: columnIndex,
                rowNumber: testRowIndex
              })
              newRowFound = true
            } else {
              testRowIndex += interval
            }
          }
        }
      }
    } else {
      const initialSearchedRow = visibleRows.findIndex((row) => {
        const searchedColumns = spreadsheetData.searchedRows.get(row.publicId)
        return searchedColumns && Array.isArray(searchedColumns) && searchedColumns.length > 0
      })
      if (initialSearchedRow !== -1) {
        const row = visibleRows[initialSearchedRow]
        const searchedColumns = spreadsheetData.searchedRows.get(row?.publicId || '')
        if (row && searchedColumns && searchedColumns.length !== 0) {
          const columnId = searchedColumns[0]
          const columnIndex = columns.findIndex((column) => column.column.publicId === columnId)
          setSpreadsheetData({
            type: 'SET_SELECTED_SEARCH_CELL',
            rowId: row.publicId,
            columnId: columnId,
            columnNumber: columnIndex,
            rowNumber: initialSearchedRow
          })
        }
      }
    }
  }

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width} zIndex={3000}>
      <div className="p-0 m-2.5 list-none">
        <div className="flex items-center">
          <Input
            className="mr-2.5 w-[200px]"
            data-tour="search-input"
            type="text"
            value={search}
            placeholder="Search Within Table"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(event.target.value)}
          />
          {search !== spreadsheetData.searchTerm && <Spinner style={{ height: iconHeight, width: iconHeight }} />}

          {!!spreadsheetData.searchTerm && (
            <div className="flex items-center flex-1">
              <Checkbox checked={filterSearchRows} onChange={(event) => setFilterSearchRows(event.target.checked)}>
                Filter Rows
              </Checkbox>
              <div className="ml-auto overflow-hidden text-body-soft text-[85%]">
                {spreadsheetData.searchFound === 0
                  ? 'No Results'
                  : `${numberWithCommas(spreadsheetData.searchFound)} Results`}
              </div>

              <div
                className="flex items-center justify-center bg-surface-sunken hover:bg-surface-strong cursor-pointer rounded transition-all ml-[5px] p-[5px]"
                onClick={() => selectNext(false)}
              >
                <ChevronUp />
              </div>
              <div
                className="flex items-center justify-center bg-surface-sunken hover:bg-surface-strong cursor-pointer rounded transition-all p-[5px]"
                onClick={() => selectNext(true)}
              >
                <ChevronDown />
              </div>

              <div
                className="flex items-center justify-center bg-surface-sunken hover:bg-surface-strong cursor-pointer rounded transition-all ml-[5px] p-[5px]"
                onClick={() => {
                  setSearch('')
                  setSpreadsheetData({ type: 'CLEAR_SEARCH' })
                  setFilterSearchRows(false)
                  setMenuState(INITIAL_CONTEXT_MENU_STATE)
                }}
              >
                <Cross />
              </div>
            </div>
          )}
        </div>
      </div>
    </Menu>
  )
}

export default SearchMenu
